import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '~/components/core';
import { Ionicons } from '~/components/core/Icons';
import { useStyles } from '~/hooks';
import { ExportState, EXPORT_MAX_ROWS } from '~/hooks/useReportExport';
import { scaleSize } from '~/constants/scale';
import { FONT_SIZE_LS, FONT_SIZE_S } from '~/constants/typography';
import { Theme } from '~/theme';
import i18n from '~/i18n';

type Props = {
    state: ExportState;
    rowsFetched: number;
    onExport: () => void;
    onCancel: () => void;
};

export default function ReportExportBar({ state, rowsFetched, onExport, onCancel }: Props) {
    const styles = useStyles(stylesGenerator);
    const running = state === 'running';

    return (
        <View style={styles.container}>
            <Pressable
                style={({ pressed }) => [styles.button, pressed ? styles.pressed : null]}
                onPress={running ? onCancel : onExport}
            >
                <Ionicons name={running ? 'close' : 'document-text-outline'} style={styles.icon} size={scaleSize(14)} />
                <Text fontType="LATO_BOLD" style={styles.label}>
                    {running ? 'cancel' : 'export-csv'}
                </Text>
            </Pressable>
            {running ? (
                <Text fontType="LATO_BOLD" style={styles.status}>
                    export-preparing
                </Text>
            ) : null}
            {running ? (
                <Text noTranslate fontType="LATO_BOLD" style={styles.count}>
                    {String(rowsFetched)}
                </Text>
            ) : null}
            {state === 'error' ? (
                <Text fontType="LATO_BOLD" style={styles.error}>
                    export-failed
                </Text>
            ) : null}
            {state === 'truncated' ? (
                <Text noTranslate fontType="LATO_BOLD" style={styles.status}>
                    {i18n.t('export-truncated', { max: String(EXPORT_MAX_ROWS) })}
                </Text>
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            paddingHorizontal: scaleSize(17),
            paddingVertical: scaleSize(8),
        },
        button: {
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-start',
            height: scaleSize(28),
            paddingHorizontal: scaleSize(12),
            borderRadius: scaleSize(5),
            backgroundColor: theme.button,
        },
        pressed: { opacity: 0.5 },
        icon: { color: theme.buttonText, paddingRight: scaleSize(7) },
        label: { fontSize: FONT_SIZE_S, color: theme.buttonText },
        status: { fontSize: FONT_SIZE_LS, color: theme.text_positionsCount, paddingLeft: scaleSize(12) },
        count: { fontSize: FONT_SIZE_LS, color: theme.text_positionsCount, paddingLeft: scaleSize(6) },
        error: { fontSize: FONT_SIZE_LS, color: theme.text_downPrice, paddingLeft: scaleSize(12) },
    });
